import { useState } from "react";
import { Card, CardBody, Button, Collapse } from "reactstrap";
import { sitesOrigin } from "env";

// What a person has to do OUTSIDE this app to make an attached domain serve.
//
// On the Domains screen rather than in a document, because the two halves of
// this are done minutes apart by the same person and the second half needs a
// value only this screen knows — the distribution hostname their edge has to
// send requests to. docs/attaching-a-domain.md is the long form.
//
// Collapsed by default: somebody returning to check a TXT record should not have
// to scroll past setup instructions to reach the table.
const PLACEHOLDER = "<your pagews distribution>.cloudfront.net";

export const Setup = () => {
  const [open, setOpen] = useState(false);
  const origin = sitesOrigin || PLACEHOLDER;

  return (
    <Card className="mb-4">
      <CardBody>
        <Button color="link" className="p-0" onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"} setup instructions
        </Button>

        <Collapse isOpen={open}>
          <div className="mt-3">
            <p>
              Attaching a domain here tells this site the hostname belongs to it.
              It does not route any traffic — that is done in your own DNS, and
              needs the two steps below.
            </p>

            <h6 className="mt-4">1. Prove you own it</h6>
            <p className="mb-2">
              Publish the TXT record shown in the table for your domain, then
              press <b>Verify</b>. Until that record is visible the domain stays{" "}
              <b>pending</b> and serves nothing — a pending domain answers exactly
              as a hostname nobody has attached, so there is no way to tell from
              the outside that it is claimed.
            </p>

            <h6 className="mt-4">2. Point the hostname at this site</h6>
            <p className="mb-2">
              Requests have to reach the distribution below, carrying that
              hostname in the path so this service can resolve which site to
              serve:
            </p>
            <pre className="small bg-light p-2">
              https://{origin}/site/by-host/&lt;your-domain&gt;/&lt;route&gt;/
            </pre>
            {!sitesOrigin && (
              // Said plainly rather than rendered as if it were a real value.
              // A hostname that looks copy-pasteable and is not is worse than an
              // obvious gap.
              <p className="text-muted small">
                This environment did not supply its distribution hostname at
                build time, so the address above is a placeholder. It is the{" "}
                <code>DistributionDomainName</code> output of the{" "}
                <code>&lt;env&gt;-pagews-cdn</code> stack.
              </p>
            )}
            <p className="mb-2">
              On Cloudflare that means a <b>Worker</b>, not a redirect and not a
              plain CNAME. A CNAME alone sends your hostname as the{" "}
              <code>Host</code> header and CloudFront rejects it; the setting that
              would rewrite it — Origin Rules' Host header override — is
              Enterprise-only on every Cloudflare plan. A Worker does the same job
              on the free tier, because the address it fetches determines the{" "}
              <code>Host</code> it sends.
            </p>
            <p className="mb-0 text-muted small">
              The Worker source, the order the Cloudflare pieces have to be
              created in, and what to check afterwards are in the repository at{" "}
              <code>docs/attaching-a-domain.md</code>.
            </p>
          </div>
        </Collapse>
      </CardBody>
    </Card>
  );
};
